import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import api from '@/api';
import { NotFoundScreen } from '@/common/ErrorScreen';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import GradedQuizSubmissionsTable from '@/tables/GradedQuizSubmissionsTable';
import PendingQuizSubmissionsTable from '@/tables/PendingQuizSubmissionsTable';
import { useQuizSubmissionsTabAccess } from '~/hooks/useStudentTabs';
import { GroupHeaderWrapper } from '~/styles/layoutElements';

/**
 * The group tab's two stacked submissions tables, in legacy's order and under
 * legacy's two headings (`groups/single/students/quiz-submissions/quizzes.php`).
 *
 * Both headings anchor `wbs_contextual_helper('students')` (`:4,20`) — the help
 * id is the SECTION, not the table, which is why both pass the same `helpKey`
 * and why it is the English section id rather than the translated title.
 */
function QuizSubmissionsScreen() {
  const { t } = useTranslation();
  const { groupName } = Route.useParams();
  const { canView, isResolved } = useQuizSubmissionsTabAccess();
  const { data: groupData } = api.getGroupBySlug.useQuery({
    path: { group_slug: groupName },
  });
  const groupId = groupData?.id ?? 0;

  // Painting the headings before the group type lands would flash them at a
  // viewer the next line refuses.
  if (!isResolved) return null;
  // The Students layout guard admits five group types and any platform admin,
  // where legacy registers this subnav on three (R2) and
  // `GET /v3/groups/{id}/quiz-submissions` gates on the same three. Without a
  // gate here the endpoint's 403 paints two empty tables, which reads as "this
  // group has no submissions" rather than as a denial — the same failure
  // `useStudyActivitiesTabAccess` was written for on the sibling tab.
  if (!canView) return <NotFoundScreen />;

  return (
    <>
      <GroupHeaderWrapper>
        <TitleWithQuestion
          title={t('wbs.pending-submissions-2')}
          helpKey="students"
        />
      </GroupHeaderWrapper>
      <PendingQuizSubmissionsTable groupId={groupId} groupName={groupName} />
      <div style={{ height: 32 }} />
      <GroupHeaderWrapper>
        <TitleWithQuestion
          title={t('lessons.all-graded-failed-submissions')}
          helpKey="students"
        />
      </GroupHeaderWrapper>
      <GradedQuizSubmissionsTable groupId={groupId} groupName={groupName} />
    </>
  );
}

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/students/quiz-submissions/quizzes/',
)({
  component: QuizSubmissionsScreen,
});
